import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { EStampMockup, MonitorMockup, ProcurementMockup, RentMockup, EventsMockup } from "@/components/mockups/DashboardMockups";

const products = [
    { slug: "estamp", name: "Colecle eStamp", tagline: "Digital Stamping & Verification", short: "Cryptographic digital stamps, QR-verified documents, immutable audit trail.", accent: "#2563EB", MockupComp: EStampMockup, num: "01" },
    { slug: "monitor", name: "Colecle Monitor", tagline: "ICT Infrastructure Intelligence", short: "Unified real-time telemetry across servers, apps, databases and APIs.", accent: "#10B981", MockupComp: MonitorMockup, num: "02" },
    { slug: "procurement", name: "Procurement & Contracts", tagline: "End-to-End Lifecycle Governance", short: "From procurement plan to contract closure — one governed platform.", accent: "#E8B923", MockupComp: ProcurementMockup, num: "03" },
    { slug: "rent", name: "Colecle Rent", tagline: "Smart Property & Rental", short: "Leases, tenants, invoicing, arrears and property portfolio analytics.", accent: "#8B5CF6", MockupComp: RentMockup, num: "04" },
    { slug: "events", name: "Colecle Events", tagline: "Enterprise Event Governance", short: "Registration, QR check-in, delegate management and live analytics.", accent: "#EC4899", MockupComp: EventsMockup, num: "05" },
];

export default function ProductsGrid() {
    return (
        <section id="products" data-testid="home-products" className="bg-[#F7F9FC] relative">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28 lg:py-36">
                <div className="grid lg:grid-cols-12 gap-8 mb-16">
                    <div className="lg:col-span-4">
                        <div className="eyebrow">Products</div>
                        <h2 className="headline text-4xl md:text-5xl lg:text-6xl mt-4 text-navy">The Colecle ecosystem.</h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 flex items-end">
                        <p className="text-slate-600 text-lg leading-relaxed">
                            Five enterprise-grade products, one unified platform. Each built to solve
                            a real business problem — and designed to work seamlessly together.
                        </p>
                    </div>
                </div>

                <div className="grid lg:grid-cols-12 gap-6">
                    {products.map((p, i) => {
                        const isLarge = i === 0 || i === 2;
                        const isMed = i === 1 || i === 3;
                        const span = isLarge ? "lg:col-span-8" : isMed ? "lg:col-span-4" : "lg:col-span-12";
                        return <ProductCard key={p.slug} p={p} span={span} />;
                    })}
                </div>
            </div>
        </section>
    );
}

function ProductCard({ p, span }) {
    const { MockupComp } = p;
    return (
        <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className={`${span} group`}
        >
            <Link
                to={`/products/${p.slug}`}
                data-testid={`product-card-${p.slug}`}
                className="spotlight block bg-white border border-slate-200/80 rounded-2xl p-6 sm:p-8 h-full relative overflow-hidden hover:border-slate-300 transition"
                onMouseMove={(e) => {
                    const r = e.currentTarget.getBoundingClientRect();
                    e.currentTarget.style.setProperty("--x", `${e.clientX - r.left}px`);
                    e.currentTarget.style.setProperty("--y", `${e.clientY - r.top}px`);
                }}
            >
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <div className="flex items-center gap-3 mb-3">
                            <span className="font-mono text-xs text-slate-400">{p.num}</span>
                            <span className="w-1 h-1 rounded-full bg-slate-300" />
                            <span className="font-mono text-[10px] tracking-widest uppercase" style={{ color: p.accent }}>{p.tagline}</span>
                        </div>
                        <h3 className="font-serif text-2xl md:text-3xl text-navy tracking-tight">{p.name}</h3>
                        <p className="text-slate-600 text-sm mt-3 max-w-md leading-relaxed">{p.short}</p>
                    </div>
                    <div className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center group-hover:bg-navy group-hover:border-navy group-hover:text-white transition flex-shrink-0">
                        <ArrowUpRight className="w-4 h-4" />
                    </div>
                </div>
                <div className="mt-6 [clip-path:inset(0_0_-40px_0)] group-hover:translate-y-[-6px] transition-transform duration-500 ease-out">
                    <MockupComp />
                </div>
            </Link>
        </motion.div>
    );
}
